import { Link, useLocation, useNavigate } from "react-router";
import logo from "../assets/images/bruvvi-logo.svg";
import { useEffect, useState } from "react";
import { routes } from "../routes";
import { Cookies } from "react-cookie";
import { localStorageItemNames } from "../common/constants";
import Breadcrumb from "../common/components/breadcrumb";
import { submenus } from "../common/config/array-config";
import { useAuth } from "../common/context/auth-context";

const Header = ({ state }) => {
    const { userSignout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location?.pathname.split("/").filter(Boolean);
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const cookies = new Cookies();
    const checkAuth = cookies.get(localStorageItemNames.TOKEN);

    useEffect(() => {
        document.body.style.overflowY = isOpen ? "hidden" : "unset";

        // Detect screen size and set isMobile accordingly
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768); // Mobile if width < 768px
        };

        handleResize(); // Initial check
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isOpen]);

    const toggleDropdown = () => {
        if (isMobile) {
            setIsDropdownOpen(!isDropdownOpen); // Toggle on click in mobile
        }
    };

    const closeToggleDropdown = () => {
        setIsDropdownOpen(false);
    };

    const renderNavLinks = () => (
        <nav className="flex flex-col md:flex-row md:items-center md:space-x-[70px]">
            <Link to={!checkAuth ? routes.SIGNUP : routes.HISTORY} onClick={closeToggleDropdown} className="py-2 text-[#62646A] hover:text-black">My trips</Link>
            <div
                className="relative"
                onMouseEnter={() => !isMobile && setIsDropdownOpen(true)} // Open on hover in web
                onMouseLeave={() => !isMobile && setIsDropdownOpen(false)} // Close on hover out in web
            >
                <button
                    onClick={toggleDropdown} // Open on click in mobile
                    className="py-2 text-[#62646A] hover:text-black flex items-center justify-between w-full md:w-auto"
                >
                    Account
                    {isMobile && (
                        <svg
                            className={`w-4 h-4 ml-2 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""
                                }`}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    )}
                </button>
                {isDropdownOpen && (
                    <div
                        className="md:absolute z-10 bg-white md:border border-[#E8ECF4] rounded-xl w-44 top-10 right-0 md:shadow-[0px_3.71px_3.71px_rgba(0,0,0,0.15)]
                        transition-all duration-300 ease-in-out transform opacity-100 scale-100"
                    >
                        <ul className="py-2 text-sm text-gray-700">
                            {submenus.map((item, index) => (
                                <li key={index}>
                                    <Link to={item?.path} onClick={closeToggleDropdown} className="block px-4 py-2 text-[#62646A] hover:text-black">
                                        {item?.title}
                                    </Link>

                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </nav>
    );

    return (
        <header className="py-4 absolute top-0 w-full z-50">
            <div className="container">
                <div className="grid grid-cols-4 gap-4 items-center">
                    <div className="md:col-span-1 col-span-2">
                        <Link to={routes.HOME}>
                            <img className="size-18" src={logo} alt="Bruvvi" />
                        </Link>
                    </div>
                    <div className="md:col-span-3 col-span-2 ml-auto flex justify-end items-center">
                        {/* Mobile Menu Button */}
                        <button className="md:hidden h-[34px]" onClick={() => setIsOpen(!isOpen)}>
                            <svg className="w-8 h-8 text-black" viewBox="0 0 24 24" fill="none">
                                {isOpen ? (
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
                                ) : (
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M5 7h14M5 12h14M5 17h10" />
                                )}
                            </svg>
                        </button>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center">{renderNavLinks()}</div>
                        <button
                            className="hidden md:flex bg-black text-white text-base ml-8 px-8 py-2 h-[52px] items-center rounded-lg hover:opacity-80"
                            onClick={() => {
                                if (checkAuth) {
                                    userSignout();
                                } else {
                                    navigate(routes.SIGNIN, { state: { item: state?.item, appointmentPayload: location?.state?.appointmentPayload } });
                                }
                            }}
                        >
                            {checkAuth ? "Sign Out" : "Sign In"}
                        </button>
                    </div>
                </div>

                {/* Mobile Sidebar Navigation */}
                <div className={`fixed top-0 left-0 w-2/3 h-full bg-white shadow-lg transition-transform overflow-y-auto ${isOpen ? "translate-x-0" : "-translate-x-full"} md:hidden`}>
                    <div className="p-6 flex flex-col space-y-6">
                        <Link to={routes.HOME}>
                            <img className="size-18" src={logo} alt="Bruvvi" />
                        </Link>
                        {renderNavLinks()}
                        <button
                            className="bg-black text-white text-base px-8 py-2 h-[52px] flex items-center justify-center rounded-lg hover:opacity-80 max-w-[140px]"
                            onClick={() => {
                                if (checkAuth) {
                                    userSignout();
                                    setIsOpen(false);
                                } else {
                                    navigate(routes.SIGNIN, { state: { item: state?.item, appointmentPayload: location?.state?.appointmentPayload } });
                                    setIsOpen(false);
                                }
                            }}
                        >
                            {checkAuth ? "Sign Out" : "Sign In"}
                        </button>
                    </div>
                </div>
                <Breadcrumb breadcrumbs={pathname} />
            </div>
        </header>
    );
};

export default Header;
